"use client";

/**
 * アプリの絵文字アイコンの出し方を画面で読む。管理UIの「アイコンの表示」（/glyphs）で選ぶ。正本は Firestore の appSettings/uiGlyphs。
 * 値は glyph-modes.ts。読めないときは line（線のアイコン）
 */
import { useSyncExternalStore } from "react";
import { isGlyphMode as isMode, type GlyphMode } from "@/lib/glyph-modes";

const CACHE_KEY = "glyphMode";

let current: GlyphMode | null = null;
let fetched = false;
const listeners = new Set<() => void>();

function read(): GlyphMode {
  if (current) return current;
  try {
    const v = localStorage.getItem(CACHE_KEY);
    if (isMode(v)) current = v;
  } catch {}
  return current ?? "line";
}

/** 最初に使われたときに一度だけ設定を読み、端末にも覚えておく（開き直したときに一瞬だけ絵文字が出ないように） */
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!fetched) {
    fetched = true;
    fetch("/api/ui-glyphs")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { mode?: unknown } | null) => {
        const mode = isMode(data?.mode) ? data.mode : "line";
        // 先に今の値と比べる（端末に書いてから比べると、いつも同じになって描き直されない）
        const changed = mode !== read();
        current = mode;
        try {
          localStorage.setItem(CACHE_KEY, mode);
        } catch {}
        if (changed) listeners.forEach((l) => l());
      })
      .catch(() => {});
  }
  return () => listeners.delete(listener);
}

export function useGlyphMode(): GlyphMode {
  return useSyncExternalStore(subscribe, read, () => "line");
}
